// Home nueva (/) — FASE 1
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronDown, Search, Calendar as CalendarIcon, X } from 'lucide-react';
import { COLORS, fmtDayShort, dateKey } from './designSystem';
import { EventsService } from './EventsService';
import { Chip, BottomSheet, EventCardLarge, VenueTile, BottomNav, PhoneShell, ColorBlockImage } from './components';
import { DayCalendar } from './DayCalendar';

const todayLabel = () => {
  const d = new Date();
  const m = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `Hoy, ${d.getDate()} ${m[d.getMonth()]}`;
};

export default function HomePageV2() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [daySheet, setDaySheet] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [e, v] = await Promise.all([EventsService.getEvents(), EventsService.getVenues()]);
      setEvents(e); setVenues(v);
    })();
  }, []);

  // Debounce ~200ms para búsqueda en vivo
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search]);

  const recomendados = useMemo(() => events.slice(0, 10), [events]);

  const searchResults = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) return null;
    const ev = events.filter((e) =>
      (e.name || '').toLowerCase().includes(q) ||
      (e.venue || '').toLowerCase().includes(q) ||
      (e.description || '').toLowerCase().includes(q)
    ).slice(0, 8);
    const vn = venues.filter((v) =>
      (v.name || '').toLowerCase().includes(q) ||
      (v.description || '').toLowerCase().includes(q)
    ).slice(0, 6);
    return { events: ev, venues: vn };
  }, [debouncedSearch, events, venues]);

  const applyDay = (d) => {
    const iso = dateKey(d);
    setDaySheet(false);
    navigate(`/eventos?fecha=${iso}`);
  };

  return (
    <PhoneShell>
      <div className="px-5 pt-6">
        <div className="flex items-center justify-between mb-5">
          <button className="flex items-center gap-1.5" onClick={() => alert('Próximamente más ciudades')}>
            <MapPin size={22} color={COLORS.primary} fill={COLORS.primary} />
            <span className="text-white text-2xl font-extrabold">Madrid</span>
            <ChevronDown size={20} color={COLORS.text} />
          </button>
          <button onClick={() => navigate('/perfil')} className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }} data-testid="home-profile-btn">
            <span className="text-white text-sm font-bold">C</span>
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 px-4 py-3 rounded-full" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <Search size={18} color={COLORS.textMuted} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && search.trim()) navigate(`/eventos?q=${encodeURIComponent(search)}`); }} placeholder="Busca clubs, eventos, viajes…" className="flex-1 bg-transparent outline-none text-sm" style={{ color: COLORS.text }} data-testid="home-search" />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Limpiar búsqueda" data-testid="home-search-clear">
              <X size={16} color={COLORS.textMuted} />
            </button>
          )}
        </div>

        {searchResults && (
          <div className="mb-6 rounded-2xl overflow-hidden" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }} data-testid="home-search-results">
            {searchResults.events.length === 0 && searchResults.venues.length === 0 ? (
              <p className="px-4 py-5 text-sm text-center" style={{ color: COLORS.textMuted }}>Sin resultados para "{debouncedSearch}"</p>
            ) : (
              <>
                {searchResults.events.length > 0 && (
                  <div className="py-2">
                    <p className="px-4 py-1 text-[11px] font-bold uppercase tracking-wide" style={{ color: COLORS.textMuted }}>Eventos</p>
                    {searchResults.events.map((e) => (
                      <button key={e.id} onClick={() => navigate(`/eventos/${e.id}`)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left active:bg-white/5" data-testid={`search-result-event-${e.id}`}>
                        <ColorBlockImage src={e.image || e.imagen_url} venueId={e.venue_id} className="w-11 h-11 shrink-0 rounded-lg" iconSize={18} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm leading-tight line-clamp-1">{e.name}</p>
                          <p className="text-xs mt-0.5" style={{ color: COLORS.textMuted }}>{e.venue}{e.fecha_inicio ? ` · ${fmtDayShort(e.fecha_inicio)}` : ''}</p>
                        </div>
                        <span className="text-white font-bold text-sm whitespace-nowrap">{e.price > 0 ? `${Number(e.price).toFixed(0)}€` : 'Gratis'}</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchResults.venues.length > 0 && (
                  <div className="py-2 border-t" style={{ borderColor: COLORS.border }}>
                    <p className="px-4 py-1 text-[11px] font-bold uppercase tracking-wide" style={{ color: COLORS.textMuted }}>Venues</p>
                    {searchResults.venues.map((v) => (
                      <button key={v.id} onClick={() => navigate(`/venues/${v.id}`)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left active:bg-white/5" data-testid={`search-result-venue-${v.id}`}>
                        <ColorBlockImage src={v.image || v.imagen_url} venueId={v.id} className="w-11 h-11 shrink-0 rounded-lg" iconSize={18} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm leading-tight line-clamp-1">{v.name}</p>
                          <p className="text-xs mt-0.5" style={{ color: COLORS.textMuted }}>{v.eventos_proximos || 0} eventos próximos</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="flex gap-2 mb-7 overflow-x-auto no-scrollbar">
          <button onClick={() => setDaySheet(true)} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap text-white" style={{ backgroundColor: COLORS.primary }} data-testid="home-date-today">
            <CalendarIcon size={14} /> {todayLabel()} <ChevronDown size={14} />
          </button>
          <Chip onClick={() => {
            const d = new Date(); d.setDate(d.getDate() + 1);
            navigate(`/eventos?fecha=${dateKey(d)}`);
          }}>Mañana</Chip>
          <Chip onClick={() => {
            const d = new Date(); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() + ((5 - dow + 7) % 7));
            navigate(`/eventos?fecha=${dateKey(d)}&finde=1`);
          }}>Este finde</Chip>
        </div>
      </div>

      <section className="mb-8">
        <div className="flex items-center justify-between px-5 mb-4">
          <h2 className="text-white text-xl font-bold">Recomendados para ti</h2>
          <button onClick={() => navigate('/eventos')} className="text-sm font-semibold" style={{ color: COLORS.coral }} data-testid="home-see-more-events">Ver más</button>
        </div>
        <div className="flex gap-3.5 overflow-x-auto no-scrollbar px-5 pb-2">
          {recomendados.length === 0 ? (
            <p className="text-sm" style={{ color: COLORS.textMuted }}>Cargando eventos…</p>
          ) : recomendados.map((e) => (
            <EventCardLarge key={e.id} event={e} onClick={() => navigate(`/eventos/${e.id}`)} />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between px-5 mb-4">
          <h2 className="text-white text-xl font-bold">Venues</h2>
          <button onClick={() => navigate('/venues')} className="text-sm font-semibold" style={{ color: COLORS.coral }} data-testid="home-see-more-venues">Ver más</button>
        </div>
        <div className="flex gap-3.5 overflow-x-auto no-scrollbar px-5 pb-2">
          {venues.slice(0, 10).map((v) => (
            <VenueTile key={v.id} venue={v} onClick={() => navigate(`/venues/${v.id}`)} />
          ))}
        </div>
      </section>

      <BottomSheet open={daySheet} onClose={() => setDaySheet(false)} title="Elige un día" testid="home-day-sheet">
        <DayCalendar onSelect={applyDay} />
      </BottomSheet>

      <BottomNav />
    </PhoneShell>
  );
}
